import React, { useCallback } from "react";
import { Upload, X, FileText } from "lucide-react";
import { useFileUpload } from "../hooks/useFileUpload";
import { LOCAL_STORAGE_KEYS } from "../lib/constants";

interface FileUploadProps {
  onFileUploaded: (key: string) => void;
  onManualEntryClick?: () => void;
  accept?: string;
  maxSize?: number; // in bytes
  className?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileUploaded,
  onManualEntryClick,
  accept = ".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx",
  maxSize = 10 * 1024 * 1024, // 10MB default
  className = "",
}) => {
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const { uploadFile, isUploading, progress } = useFileUpload({
    onSuccess: (key) => {
      onFileUploaded(key);
      setError(null);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file size
      if (file.size > maxSize) {
        setError(`File size must be less than ${maxSize / 1024 / 1024}MB`);
        return;
      }

      // Validate file type
      const acceptedTypes = accept.split(",").map((type) => type.trim());
      const fileExtension = `.${file.name.split(".").pop()}`;
      if (!acceptedTypes.includes(fileExtension)) {
        setError(`File type must be one of: ${accept}`);
        return;
      }

      setSelectedFile(file);
      setError(null);

      try {
        await uploadFile(file);
      } catch {
        // Error is handled by the hook
      }
    },
    [accept, maxSize, uploadFile]
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.UPLOADED_QUOTE);
  }, []);

  return (
    <div
      className={`border-2 border-dashed border-gray-200 rounded-lg p-6 text-center ${className}`}
    >
      {!selectedFile ? (
        <div className="pb-4">
          <input
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
            id="quote-upload"
            disabled={isUploading}
          />
          <label
            htmlFor="quote-upload"
            className="btn-primary cursor-pointer inline-flex items-center justify-center"
          >
            <Upload className="w-5 h-5 mr-2" />
            {isUploading ? "Uploading..." : "Upload Quote"}
          </label>
          <p className="mt-2 text-sm text-gray-500">
            Upload your mortgage quote (PDF, images, or spreadsheets)
          </p>
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          {/* {onManualEntryClick && (
            <button onClick={onManualEntryClick} className="btn-tertiary mt-4">
              Enter information manually instead
            </button>
          )} */}
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <span className="text-sm text-gray-900">{selectedFile.name}</span>
          </div>
          {!isUploading && (
            <button
              onClick={handleRemoveFile}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
      {isUploading && (
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-primary h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
